import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  TrendingUp,
  DollarSign,
  UserPlus,
  Building2,
  ChevronLeft,
  ChevronRight,
  Globe,
  Phone,
  Mail,
  MapPin,
  Percent,
  Hash,
  Ban,
  Play,
  EyeOff,
  KeyRound,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const StatCard = ({ title, value, icon: Icon, color, prefix = '' }) => (
  <div className="bg-card rounded-lg border border-border p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-1">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
      </div>
      <div className={cn('p-3 rounded-lg', color)}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Spain', 'Italy', 'Netherlands', 'Sweden', 'Norway', 'Denmark',
  'Switzerland', 'Austria', 'Belgium', 'Ireland', 'Portugal', 'Finland',
  'New Zealand', 'Singapore', 'Japan', 'South Korea', 'India', 'Brazil',
  'Mexico', 'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Kuwait',
  'Bahrain', 'Oman', 'Egypt', 'South Africa', 'Nigeria', 'Other'
];

const PartnerForm = ({ formData, setFormData, showPassword, setShowPassword, isEdit = false }) => (
  <Tabs defaultValue="basic" className="w-full">
    <TabsList className="grid w-full grid-cols-3">
      <TabsTrigger value="basic">Basic Info</TabsTrigger>
      <TabsTrigger value="commission">Commission</TabsTrigger>
      <TabsTrigger value="optional">Optional</TabsTrigger>
    </TabsList>

    <TabsContent value="basic" className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Partner Name *</Label>
          <Input
            value={formData.partner_name}
            onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
            placeholder="John Doe"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Company / Gym Name *</Label>
          <Input
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            placeholder="Fitness Hub"
            className="mt-1.5"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Email *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="partner@example.com"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 234 567 8900"
            className="mt-1.5"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Country *</Label>
          <Select
            value={formData.country}
            onValueChange={(v) => setFormData({ ...formData, country: v })}
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select
            value={formData.status}
            onValueChange={(v) => setFormData({ ...formData, status: v })}
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Portal Password */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-semibold">Partner Portal Password</Label>
        </div>
        {isEdit && (
          <p className="text-xs text-muted-foreground mb-2">Leave blank to keep the current password unchanged.</p>
        )}
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder={isEdit ? 'Enter new password to change…' : 'Set portal login password'}
            className="pr-10"
            data-testid="partner-portal-password"
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {!isEdit && (
          <p className="text-xs text-muted-foreground mt-1">
            Min. 6 characters. The partner will use this to log in at <span className="font-mono">/partner/login</span>.
          </p>
        )}
      </div>
    </TabsContent>

    <TabsContent value="commission" className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>AI Commission %</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.ai_commission_percent}
              onChange={(e) => setFormData({ ...formData, ai_commission_percent: Number(e.target.value) })}
            />
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
        <div>
          <Label>Premium Member Commission %</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.premium_commission_percent}
              onChange={(e) => setFormData({ ...formData, premium_commission_percent: Number(e.target.value) })}
            />
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Commission percentages determine the partner's earnings from each subscription type.
      </p>
    </TabsContent>

    <TabsContent value="optional" className="space-y-4 mt-4">
      <div>
        <Label>Website</Label>
        <Input
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://example.com"
          className="mt-1.5"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Facebook</Label>
          <Input
            value={formData.social_media?.facebook || ''}
            onChange={(e) => setFormData({ ...formData, social_media: { ...formData.social_media, facebook: e.target.value } })}
            placeholder="facebook.com/..."
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Instagram</Label>
          <Input
            value={formData.social_media?.instagram || ''}
            onChange={(e) => setFormData({ ...formData, social_media: { ...formData.social_media, instagram: e.target.value } })}
            placeholder="@username"
            className="mt-1.5"
          />
        </div>
      </div>
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium mb-3">Bank / Payout Details</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Bank Name</Label>
            <Input
              value={formData.bank_details?.bank_name || ''}
              onChange={(e) => setFormData({ ...formData, bank_details: { ...formData.bank_details, bank_name: e.target.value } })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Account Holder</Label>
            <Input
              value={formData.bank_details?.account_holder || ''}
              onChange={(e) => setFormData({ ...formData, bank_details: { ...formData.bank_details, account_holder: e.target.value } })}
              className="mt-1.5"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Account Number / IBAN</Label>
            <Input
              value={formData.bank_details?.account_number || formData.bank_details?.iban || ''}
              onChange={(e) => setFormData({ ...formData, bank_details: { ...formData.bank_details, account_number: e.target.value } })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>PayPal Email</Label>
            <Input
              type="email"
              value={formData.bank_details?.paypal_email || ''}
              onChange={(e) => setFormData({ ...formData, bank_details: { ...formData.bank_details, paypal_email: e.target.value } })}
              className="mt-1.5"
            />
          </div>
        </div>
      </div>
    </TabsContent>
  </Tabs>
);

const PartnersPage = () => {
  const { user, canAction } = useCompanyAdminAuth();
  const isAgent   = Number(user?.user_type) === 3;
  const canAdd    = canAction('partners', 'add');
  const canEdit   = canAction('partners', 'edit');
  const canDelete = canAction('partners', 'delete');

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    partner_name: '',
    company_name: '',
    email: '',
    phone: '',
    country: '',
    ai_commission_percent: 10,
    premium_commission_percent: 10,
    status: 'active',
    website: '',
    social_media: { facebook: '', instagram: '', twitter: '', linkedin: '' },
    bank_details: { bank_name: '', account_holder: '', account_number: '', iban: '', swift_code: '', paypal_email: '' },
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const resetForm = () => {
    setFormData({
      partner_name: '',
      company_name: '',
      email: '',
      phone: '',
      country: '',
      ai_commission_percent: 10,
      premium_commission_percent: 10,
      status: 'active',
      website: '',
      social_media: { facebook: '', instagram: '', twitter: '', linkedin: '' },
      bank_details: { bank_name: '', account_holder: '', account_number: '', iban: '', swift_code: '', paypal_email: '' },
      password: '',
    });
    setShowPassword(false);
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await companyAdminCustomersAPI.getPartnerStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim(),
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };
      const res = await companyAdminCustomersAPI.getPartners(params);
      setPartners(res.data.partners || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        pages: res.data.pages,
      }));
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter]);

  useEffect(() => {
    loadStats();
    loadPartners();
  }, [loadStats, loadPartners]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewDetail = async (partner) => {
    setSelectedPartner(partner);
    setDetailModalOpen(true);
    try {
      const res = await companyAdminCustomersAPI.getPartnerDetail(partner.partner_id);
      setPartnerDetail(res.data);
    } catch (error) {
      toast.error('Failed to load partner details');
    }
  };

  const handleOpenEdit = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      partner_name: partner.partner_name || '',
      company_name: partner.company_name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      country: partner.country || '',
      ai_commission_percent: partner.ai_commission_percent || 10,
      premium_commission_percent: partner.premium_commission_percent || 10,
      status: partner.status || 'active',
      website: partner.website || '',
      social_media: partner.social_media || { facebook: '', instagram: '', twitter: '', linkedin: '' },
      bank_details: partner.bank_details || { bank_name: '', account_holder: '', account_number: '', iban: '', swift_code: '', paypal_email: '' },
      password: '',
    });
    setShowPassword(false);
    setEditModalOpen(true);
  };

  const handleOpenCreate = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const handleCreatePartner = async () => {
    if (!formData.partner_name || !formData.company_name || !formData.email || !formData.phone || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setActionLoading(true);
    try {
      const { password, ...partnerData } = formData;
      const res = await companyAdminCustomersAPI.createPartner(partnerData);
      const newPartnerId = res.data?.partner?.partner_id;
      if (password && newPartnerId) {
        await companyAdminCustomersAPI.setPartnerPassword(newPartnerId, { password });
      }
      toast.success('Partner created successfully');
      setCreateModalOpen(false);
      resetForm();
      loadPartners();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create partner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePartner = async () => {
    if (!selectedPartner) return;
    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setActionLoading(true);
    try {
      const { password, ...partnerData } = formData;
      await companyAdminCustomersAPI.updatePartner(selectedPartner.partner_id, partnerData);
      if (password) {
        await companyAdminCustomersAPI.setPartnerPassword(selectedPartner.partner_id, { password });
      }
      toast.success('Partner updated successfully');
      setEditModalOpen(false);
      loadPartners();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update partner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.deletePartner(selectedPartner.partner_id);
      toast.success('Partner deleted successfully');
      setDeleteModalOpen(false);
      loadPartners();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete partner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (partner) => {
    try {
      await companyAdminCustomersAPI.togglePartnerStatus(partner.partner_id);
      toast.success(`Partner ${partner.status === 'active' ? 'suspended' : 'activated'}`);
      loadPartners();
      loadStats();
    } catch (error) {
      toast.error('Failed to toggle partner status');
    }
  };

  const handleRegenerateCode = async (partner) => {
    try {
      const res = await companyAdminCustomersAPI.regeneratePartnerCode(partner.partner_id);
      toast.success(`New referral code: ${res.data.referral_code}`);
      loadPartners();
    } catch (error) {
      toast.error('Failed to regenerate referral code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };



  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Partners</h1>
            <p className="text-muted-foreground mt-1">Manage your affiliate partners and referrals</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { loadStats(); loadPartners(); }} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canAdd && (
              <Button onClick={handleOpenCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Add Partner
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard title="Total Partners" value={stats.total_partners} icon={Users} color="bg-slate-500" />
            <StatCard title="Active Partners" value={stats.active_partners} icon={CheckCircle} color="bg-green-500" />
            <StatCard title="Total Signups" value={stats.total_signups} icon={UserPlus} color="bg-blue-500" />
            <StatCard title="Paid Customers" value={stats.total_paid_customers} icon={TrendingUp} color="bg-purple-500" />
            <StatCard title="Total Earnings" value={formatCurrency(stats.total_earnings)} icon={DollarSign} color="bg-emerald-500" />
            <StatCard title="Pending Balance" value={formatCurrency(stats.pending_balance)} icon={DollarSign} color="bg-amber-500" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm mb-1.5 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <Label className="text-sm mb-1.5 block">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit">Search</Button>
          </form>
        </div>

        {/* Partners Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Partner</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Referral Code</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Commission</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Signups</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Earnings</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Status</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : partners.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No partners found</td></tr>
                ) : (
                  partners.map((partner) => (
                    <tr key={partner.partner_id} className="border-b border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{partner.partner_name}</p>
                          <p className="text-sm text-muted-foreground">{partner.company_name}</p>
                          <p className="text-xs text-muted-foreground">{partner.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <code className="bg-muted px-2 py-1 rounded text-sm font-mono">{partner.referral_code}</code>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(partner.referral_code)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <p>AI: {partner.ai_commission_percent}%</p>
                          <p>Premium: {partner.premium_commission_percent}%</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{partner.total_signups || 0}</p>
                          <p className="text-xs text-muted-foreground">{partner.total_paid_customers || 0} paid</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{formatCurrency(partner.total_earnings)}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(partner.pending_balance)} pending</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                          partner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        )}>
                          {partner.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {partner.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetail(partner)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {canEdit && (
                              <>
                                <DropdownMenuItem onClick={() => handleOpenEdit(partner)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit Partner
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRegenerateCode(partner)}>
                                  <Hash className="w-4 h-4 mr-2" /> Regenerate Code
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleToggleStatus(partner)}>
                                  {partner.status === 'active' ? (
                                    <><Ban className="w-4 h-4 mr-2" /> Suspend</>
                                  ) : (
                                    <><Play className="w-4 h-4 mr-2" /> Activate</>
                                  )}
                                </DropdownMenuItem>
                              </>
                            )}
                            {canDelete && (
                              <DropdownMenuItem
                                onClick={() => { setSelectedPartner(partner); setDeleteModalOpen(true); }}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
            <DialogDescription>Fill in the details to create a new affiliate partner.</DialogDescription>
          </DialogHeader>
          <PartnerForm formData={formData} setFormData={setFormData} showPassword={showPassword} setShowPassword={setShowPassword} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePartner} disabled={actionLoading}>
              {actionLoading ? 'Creating...' : 'Create Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>Update partner information.</DialogDescription>
          </DialogHeader>
          <PartnerForm formData={formData} setFormData={setFormData} showPassword={showPassword} setShowPassword={setShowPassword} isEdit />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdatePartner} disabled={actionLoading}>
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Details</DialogTitle>
            <DialogDescription>{selectedPartner?.partner_name} - {selectedPartner?.company_name}</DialogDescription>
          </DialogHeader>
          {partnerDetail ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="referrals">Referrals</TabsTrigger>
                <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">{partnerDetail.partner?.total_signups || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Signups</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">{partnerDetail.partner?.total_paid_customers || 0}</p>
                    <p className="text-sm text-muted-foreground">Paid Customers</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-purple-600">{formatCurrency(partnerDetail.partner?.total_earnings)}</p>
                    <p className="text-sm text-muted-foreground">Total Earnings</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-3">Contact Info</h4>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {partnerDetail.partner?.email}</p>
                      <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {partnerDetail.partner?.phone}</p>
                      <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {partnerDetail.partner?.country}</p>
                      {partnerDetail.partner?.website && (
                        <p className="flex items-center gap-2"><Globe className="w-4 h-4" /> {partnerDetail.partner?.website}</p>
                      )}
                    </div>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-3">Commission Rates</h4>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center gap-2"><Percent className="w-4 h-4" /> AI: {partnerDetail.partner?.ai_commission_percent}%</p>
                      <p className="flex items-center gap-2"><Percent className="w-4 h-4" /> Premium: {partnerDetail.partner?.premium_commission_percent}%</p>
                      <p className="flex items-center gap-2"><Hash className="w-4 h-4" /> Code: <code className="bg-gray-200 px-2 py-0.5 rounded">{partnerDetail.partner?.referral_code}</code></p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="referrals" className="mt-4">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {partnerDetail.recent_referrals?.length > 0 ? (
                    partnerDetail.recent_referrals.map((ref, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">{ref.customer_id?.name || 'Customer'}</p>
                          <p className="text-xs text-muted-foreground">{ref.customer_id?.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">{ref.subscription_type || 'Free'}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(ref.signup_date)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No referrals yet</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="withdrawals" className="mt-4">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {partnerDetail.recent_withdrawals?.length > 0 ? (
                    partnerDetail.recent_withdrawals.map((w, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">{formatCurrency(w.amount)}</p>
                          <p className="text-xs text-muted-foreground">{w.payment_method}</p>
                        </div>
                        <div className="text-right">
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            w.status === 'completed' ? 'bg-green-100 text-green-700' :
                            w.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            w.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-muted'
                          )}>
                            {w.status}
                          </span>
                          <p className="text-xs text-muted-foreground mt-1">{formatDate(w.requested_at)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No withdrawals yet</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Partner</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedPartner?.partner_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePartner} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CompanyAdminLayout>
  );
};

export default PartnersPage;
